import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getServiceAreaBySlug } from '../data/serviceAreas';

const { FiArrowLeft, FiMapPin, FiUsers, FiCheckCircle, FiArrowRight, FiPhone, FiMail, FiStar, FiTrendingUp, FiGlobe, FiTarget } = FiIcons;

const ServiceAreaDetail = () => {
  const { slug } = useParams();
  const area = getServiceAreaBySlug(slug);

  if (!area) {
    return <Navigate to="/service-areas" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Back to Service Areas Button */}
      <div className="fixed top-24 left-8 z-40">
        <Link
          to="/service-areas"
          className="flex items-center space-x-2 bg-white/95 backdrop-blur-md text-slate-700 px-4 py-2 rounded-full shadow-lg hover:bg-white hover:shadow-xl transition-all duration-300 border border-slate-200"
        >
          <SafeIcon icon={FiArrowLeft} className="text-sm" />
          <span className="font-medium">Back to Service Areas</span>
        </Link>
      </div>

      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900 overflow-hidden pt-32">
        <div className="absolute inset-0 opacity-20">
          <img
            src={area.image}
            alt={area.name}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <div className="mb-6">
              <span className="bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                Service Area
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Web Design Services in {area.name}
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mb-8">
              {area.description}
            </p>

            {/* Area Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8 max-w-2xl">
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiMapPin} className="text-orange-400" />
                <div>
                  <div className="text-sm text-slate-400">Region</div>
                  <div className="font-semibold">{area.region}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiUsers} className="text-orange-400" />
                <div>
                  <div className="text-sm text-slate-400">Population</div>
                  <div className="font-semibold">{area.population}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiTrendingUp} className="text-orange-400" />
                <div>
                  <div className="text-sm text-slate-400">Clients</div>
                  <div className="font-semibold">{area.clients.length}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiStar} className="text-orange-400" />
                <div>
                  <div className="text-sm text-slate-400">Rating</div>
                  <div className="font-semibold">5.0 ★</div>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="group bg-orange-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-orange-600 transition-all duration-300 inline-flex items-center justify-center space-x-3"
              >
                <span>Get Your Free Quote</span>
                <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              {/* <a
                href="tel:+447769061291"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-slate-900 transition-all duration-300 inline-flex items-center justify-center space-x-3"
              >
                <SafeIcon icon={FiPhone} />
                <span>Call: 07769061291</span>
              </a> */}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Services Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                <SafeIcon icon={FiGlobe} className="inline mr-3 text-orange-500" />
                Our {area.name} Web Design Services
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                We understand the unique business landscape in {area.name} and create websites that resonate with local customers while attracting clients from across {area.region}.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {area.services.map((service, index) => (
                  <div key={index} className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg">
                    <SafeIcon icon={FiCheckCircle} className="text-orange-500 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{service}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-2 bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors duration-300"
              >
                <span>Start Your Project</span>
                <SafeIcon icon={FiArrowRight} />
              </Link>
            </motion.div>

            {/* Key Points */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-slate-50 p-8 rounded-2xl"
            >
              <h3 className="text-2xl font-bold text-slate-900 mb-6">
                <SafeIcon icon={FiTarget} className="inline mr-3 text-orange-500" />
                Why Choose Us for {area.name}?
              </h3>
              <div className="space-y-4">
                {area.keyPoints.map((point, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-slate-700 leading-relaxed">{point}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Local Clients Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Trusted by {area.name} Businesses
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We're proud to have helped these local {area.name} businesses succeed online with professional web design and digital marketing.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {area.clients.map((client, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl shadow-lg border border-slate-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="text-lg font-semibold text-slate-900">{client}</div>
                  <div className="text-orange-600">★★★★★</div>
                </div>
                <div className="text-slate-600 text-sm mb-2">
                  <SafeIcon icon={FiMapPin} className="inline mr-1" />
                  {area.name}, {area.region}
                </div>
                <div className="text-slate-500 text-sm">
                  Professional website design & local SEO services
                </div>
              </motion.div>
            ))}
          </div>

          {/* Testimonial */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="bg-orange-50 p-8 rounded-2xl border-l-4 border-orange-500 max-w-4xl mx-auto"
          >
            <div className="text-orange-600 mb-4 text-center">★★★★★</div>
            <blockquote className="text-slate-700 text-lg italic text-center mb-6">
              "WeDesignedIt created exactly what we needed for our {area.name} business. The website looks professional and we're getting more enquiries than ever before. Their understanding of the local market really shows."
            </blockquote>
            <div className="text-center">
              <div className="font-semibold text-slate-900">Local Business Owner</div>
              <div className="text-slate-600 text-sm">{area.name}, {area.region}</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Grow Your {area.name} Business Online?
            </h2>
            <p className="text-xl text-slate-300 mb-8">
              Join the successful {area.name} businesses we've already helped. Get a free consultation and quote for your web design project.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="bg-slate-800 p-6 rounded-xl">
                <SafeIcon icon={FiPhone} className="text-orange-400 text-2xl mb-3 mx-auto" />
                <h3 className="font-semibold mb-2">Call Us Today</h3>
                <p className="text-slate-300 text-sm mb-3">Speak directly with our team</p>
                {/* <a
                  href="tel:+447769061291"
                  className="text-orange-400 hover:text-orange-300 font-medium"
                >
                  07769061291
                </a> */}
              </div>
              <div className="bg-slate-800 p-6 rounded-xl">
                <SafeIcon icon={FiMail} className="text-orange-400 text-2xl mb-3 mx-auto" />
                <h3 className="font-semibold mb-2">Email Enquiry</h3>
                <p className="text-slate-300 text-sm mb-3">Get a detailed proposal</p>
                <a
                  href="mailto:hello@wedesignedit.co.uk"
                  className="text-orange-400 hover:text-orange-300 font-medium"
                >
                  hello@wedesignedit.co.uk
                </a>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-orange-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-orange-600 transition-all duration-300 inline-flex items-center justify-center space-x-3"
              >
                <span>Get Free Quote</span>
                <SafeIcon icon={FiArrowRight} />
              </Link>
              <Link
                to="/projects"
                className="border border-slate-600 text-slate-300 px-8 py-4 rounded-full font-semibold hover:bg-slate-800 transition-colors duration-300"
              >
                View Our Work
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServiceAreaDetail;